import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import Reveal from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = { name: "", email: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm(initialState);
      toast.success("Thank you \u2014 your message has been sent.");
    } catch (err) {
      console.error(err);
      toast.error("Could not send. Please email support@serenatti.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="pt-32 md:pt-40 pb-24">
      <section className="px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <SectionHeading
              overline="Contact"
              title="Questions or feedback?"
              subtitle="Questions or feedback about the articles? I'd love to hear from you."
              align="center"
              className="mx-auto text-center"
            />
          </Reveal>

          <Reveal delay={120}>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="mt-14 bg-white border border-navy/10 rounded-sm p-8 md:p-10"
            >
              {submitted ? (
                <div data-testid="contact-success" className="py-10 text-center">
                  <p className="text-xs uppercase tracking-[0.25em] text-gold">Thank you</p>
                  <h3 className="mt-3 font-heading text-3xl text-navy">Your message has been sent.</h3>
                  <p className="mt-4 text-charcoal/75">I&rsquo;ll get back to you at the email you provided.</p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-send-another"
                    className="mt-8 px-6 py-3 border border-navy text-navy text-xs tracking-[0.22em] uppercase hover:bg-navy hover:text-white transition-all rounded-sm"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  <label className="block">
                    <span className="block text-[11px] tracking-[0.22em] uppercase text-charcoal/70 mb-2">Name *</span>
                    <input
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={update("name")}
                      className="w-full bg-transparent border-0 border-b border-navy/20 rounded-none px-0 py-3 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-gold transition-colors"
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] tracking-[0.22em] uppercase text-charcoal/70 mb-2">Email *</span>
                    <input
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className="w-full bg-transparent border-0 border-b border-navy/20 rounded-none px-0 py-3 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-gold transition-colors"
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] tracking-[0.22em] uppercase text-charcoal/70 mb-2">Message *</span>
                    <textarea
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      rows={6}
                      className="w-full bg-transparent border-0 border-b border-navy/20 rounded-none px-0 py-3 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-gold transition-colors resize-none"
                      required
                    />
                  </label>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                    <a
                      href="mailto:support@serenatti.com"
                      data-testid="contact-email-link"
                      className="inline-flex items-center gap-2 text-sm text-navy hover:text-gold"
                    >
                      <Mail size={16} /> support@serenatti.com
                    </a>
                    <button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={submitting}
                      className="inline-flex items-center justify-center px-8 py-3.5 bg-navy text-white text-xs tracking-[0.22em] uppercase font-medium rounded-sm hover:bg-navy/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all hover-lift"
                    >
                      {submitting ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </Reveal>

          <Reveal>
            <p data-testid="contact-disclaimer" className="mt-10 text-sm text-charcoal/70 text-center leading-relaxed max-w-2xl mx-auto">
              This site is educational and does not offer medical advice, products, or services.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
